import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Car,
  CheckCircle2,
  GraduationCap,
  HelpCircle,
  Loader2,
  Sparkles,
} from 'lucide-react';
import {
  cancelLesson,
  fetchExam,
  fetchHistory,
  fetchLessons,
  fetchProgress,
  fetchSlots,
} from '../api/candidate';
import { useAuthStore } from '../store/auth';
import { durationLabel, monitorInitials, slotLabel } from '../lib/format';
import dayjs from '../lib/dates';
import PayableInvoices from '../components/PayableInvoices';

const TIPS = [
  'Réservez vos leçons en un clic parmi les créneaux ouverts par votre auto-école : votre moniteur est prévenu automatiquement.',
  'Révisez régulièrement les vérifications intérieures et extérieures du véhicule : elles tombent souvent à l’examen.',
  'Espacez raisonnablement vos leçons pour bien assimiler : mieux vaut de la régularité que de longues sessions rapprochées.',
  'Un créneau vous convient ? Réservez-le tôt : les places partent vite en période d’examen.',
  'En cas d’imprévu, annulez votre leçon depuis l’app : le créneau se libère aussitôt pour un autre élève.',
];

export default function DashboardView({
  onReserve,
}: {
  onReserve: () => void;
}) {
  const candidate = useAuthStore((s) => s.candidate);
  const queryClient = useQueryClient();

  const lessonsQuery = useQuery({
    queryKey: ['candidate', 'lessons'],
    queryFn: fetchLessons,
  });
  const slotsQuery = useQuery({
    queryKey: ['candidate', 'slots'],
    queryFn: fetchSlots,
  });
  const progressQuery = useQuery({
    queryKey: ['candidate', 'progress'],
    queryFn: fetchProgress,
  });
  const examQuery = useQuery({
    queryKey: ['candidate', 'exam'],
    queryFn: fetchExam,
  });
  const historyQuery = useQuery({
    queryKey: ['candidate', 'history'],
    queryFn: fetchHistory,
  });

  const cancelMutation = useMutation({
    mutationFn: (lessonId: string) => cancelLesson(lessonId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'lessons'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate', 'slots'] });
    },
  });

  const lessons = lessonsQuery.data ?? [];
  // Prochaine leçon = première leçon non terminée (la liste inclut désormais
  // les leçons passées récentes pour l'historique de présence).
  const nowMs = Date.now();
  const nextLesson =
    lessons.find((l) => new Date(l.endsAt).getTime() >= nowMs) ?? null;
  const openSlots = slotsQuery.data?.length ?? 0;

  const [tipIndex, setTipIndex] = useState(0);
  const tip = TIPS[tipIndex % TIPS.length];
  const [showCancelInfo, setShowCancelInfo] = useState(false);

  const exam = examQuery.data ?? null;
  const examDays = exam ? dayjs(exam.date).startOf('day').diff(dayjs().startOf('day'), 'day') : null;
  const history = historyQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Bandeau examen (si un examen est programmé dans les 21 jours) */}
      {exam && examDays !== null && examDays >= 0 && examDays <= 21 && (
        <div className="-mx-6 -mt-8 mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="px-6 py-2.5">
            <p className="text-sm font-medium">
              🎓 Votre examen approche —{' '}
              <span className="font-semibold">
                {dayjs(exam.date).format('dddd D MMMM')}
                {exam.center ? ` · ${exam.center}` : ''}
              </span>
              {examDays === 0
                ? " · c'est aujourd'hui !"
                : ` · dans ${examDays} jour${examDays > 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-slate-900">
          Bonjour {candidate?.firstName ?? ''} 👋
        </h1>
        <p className="text-slate-600">
          Voici l’essentiel de votre formation à la conduite.
        </p>
      </div>

      {/* Factures à régler (toujours visible, même si l'onglet paiements est masqué) */}
      <PayableInvoices />

      {/* Grille 3 cartes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Prochaine leçon */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Prochaine leçon
            </span>
            <div className="flex items-center gap-2">
              {nextLesson && (
                <span className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold">
                  Confirmée
                </span>
              )}
              {nextLesson && !nextLesson.bookedOnline && (
                <button
                  type="button"
                  onClick={() => setShowCancelInfo((v) => !v)}
                  className="w-6 h-6 rounded-full border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-colors"
                  aria-label="Annuler cette leçon ?"
                  title="Annuler cette leçon ?"
                >
                  <HelpCircle size={15} />
                </button>
              )}
            </div>
          </div>
          {nextLesson ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Car className="text-indigo-600" size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {slotLabel(nextLesson.startsAt, nextLesson.endsAt)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {nextLesson.instructorName
                      ? `Avec ${nextLesson.instructorName}`
                      : 'Moniteur à confirmer'}{' '}
                    · {durationLabel(nextLesson.startsAt, nextLesson.endsAt)}
                  </div>
                </div>
              </div>
              {nextLesson.bookedOnline && (
                <button
                  onClick={() => cancelMutation.mutate(nextLesson.id)}
                  disabled={cancelMutation.isPending}
                  className="w-full px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-60 transition-colors text-sm font-medium"
                >
                  Annuler
                </button>
              )}
              {!nextLesson.bookedOnline && showCancelInfo && (
                <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 leading-relaxed">
                  Vous souhaitez annuler ? Cette leçon a été planifiée par votre
                  auto-école, veuillez la contacter pour tout empêchement.
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-500 py-2">
              Aucune leçon à venir pour le moment.
            </div>
          )}
        </div>

        {/* Réserver */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Réserver
            </span>
            <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
              {openSlots} dispo{openSlots > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Calendar className="text-indigo-600" size={20} />
            </div>
            <div className="min-w-0 text-sm text-slate-600">
              Des créneaux sont ouverts par votre auto-école.
            </div>
          </div>
          <button
            onClick={onReserve}
            className="mt-auto w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-semibold shadow-sm"
          >
            Réserver une leçon
          </button>
        </div>

        {/* Mon examen */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Mon examen
            </span>
            {exam && (
              <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
                Programmé
              </span>
            )}
          </div>
          {exam ? (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">
                {examDays !== null && examDays >= 0 ? `J-${examDays}` : (
                  <GraduationCap size={20} />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">
                  {dayjs(exam.date).format('ddd D MMM')}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {exam.center ?? 'Centre à confirmer'}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="text-slate-400" size={20} />
              </div>
              <div className="text-sm text-slate-500">
                Aucun examen programmé pour l’instant.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ma progression par compétence (depuis les bilans moniteurs) */}
      {(progressQuery.data?.competencies.length ?? 0) > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 mb-1">
            <h2 className="text-slate-900">Ma progression par compétence</h2>
            {progressQuery.data?.readyForExam && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
                <CheckCircle2 size={13} />
                Prêt(e) pour l’examen
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Évaluée par vos moniteurs à chaque leçon.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-5">
            {progressQuery.data?.competencies.map((c) => (
              <div key={c.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-600">{c.label}</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {c.percent} %
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all"
                    style={{ width: `${c.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conseil Permio */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="bg-white/20 rounded-xl p-2.5 flex-shrink-0 self-start">
            <Sparkles size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold mb-1">Conseil Permio</div>
            <p className="text-sm text-white/90">{tip}</p>
          </div>
          <button
            onClick={() => setTipIndex((i) => i + 1)}
            className="self-start px-3.5 py-2 bg-white text-indigo-700 hover:bg-indigo-50 text-xs font-semibold rounded-lg shadow-sm transition-colors flex-shrink-0"
          >
            Autres conseils
          </button>
        </div>
      </div>

      {/* Mes dernières leçons (bilans moniteurs) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-slate-900 mb-1">Mes dernières leçons</h2>
        <p className="text-sm text-slate-500 mb-5">
          Avec la note de votre moniteur.
        </p>
        {historyQuery.isLoading ? (
          <div className="flex items-center text-slate-500 text-sm py-4">
            <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">
            Aucun bilan de leçon pour le moment. Vos moniteurs les rempliront au
            fil de votre formation.
          </p>
        ) : (
          <div className="space-y-4">
            {history.map((b, i) => (
              <div key={b.id}>
                {i > 0 && <div className="border-t border-slate-100 mb-4" />}
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {monitorInitials(b.instructorName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-slate-900">
                        {dayjs(b.date).format('ddd D MMM · HH:mm')}
                      </span>
                      {b.instructorName && (
                        <span className="text-xs text-slate-500">
                          {b.instructorName}
                        </span>
                      )}
                      {b.rating != null && (
                        <span className="text-xs text-amber-600 font-medium">
                          {'★'.repeat(b.rating)}
                          <span className="text-slate-300">
                            {'★'.repeat(Math.max(0, 5 - b.rating))}
                          </span>
                        </span>
                      )}
                    </div>
                    {b.summary && (
                      <p className="text-sm text-slate-600 mt-0.5">
                        « {b.summary} »
                      </p>
                    )}
                  </div>
                  {b.readyForExam && (
                    <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-medium flex-shrink-0">
                      Prêt(e)
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
